// src/encounter/ap-inline-narrative.tsx
//
// PROD-3043 · frame v2 only (`inNote`; design desk D-73, 6 Oct). The A&P narrative ("Added notes",
// `PatientAssessment.notes` + `notePlainText`) typed INLINE above the diagnoses, as a note line — not in
// the "Add Text Notes" pop-up, which frame v2 never opens. Switch OFF keeps the pop-up and the read-back
// card exactly as before (assessment-plan-view.tsx).
//
// SAME FIELD, SAME SAVE PATH. The line reads the pair the page already holds and writes it back through
// the same `onAssessmentNoteChange(plain, delta)` the pop-up's Save called, so the page's A&P autosave
// (PatientAssessmentPlan) persists it unchanged. Nothing is renamed and no new field exists.
//
// SAME PHRASES. The scoped list the pop-up fetched (`useNoteMacros(macroScope)`, TS-4) and the pop-up's
// editor id (`ap-narrative`); piece phrases ON in frame v2 (PROD-2947 Part 2; the pop-up keeps `?ef=phrasePieces`). Lane A owns
// the scope and the names; this consumes them as they are.
//
// THE LOSSLESS RULE (D-73 amendment): the line edits the narrative only when it is plain text plus phrase
// pieces (`readInlineNarrative`). A formatted narrative (bold, lists, colours, images from Flutter's rich
// editor) shows read-only here with "Edit", which opens today's dialog — so nothing the line cannot write
// back is ever rewritten, and an unedited narrative round-trips unchanged.
//
// RULE 38: read-only after the provider signature (the page passes `readOnly`, and the "Not part of an
// amendment" notice while amending, as for the rest of the A&P). Read-only means no Edit either.
import { useState } from 'react';
import { Button, NoteLine } from '@scripsteam/scrips-signal-ds';
import { lineSegsToWire, readInlineNarrative } from './lib/narrative-line-wire';
import type { NoteMacro } from './lib/smart-phrase';
import type { ChartReads } from './lib/to-chart-snapshot';
import { useNoteMacros, type NoteMacroScope } from './services/use-note-macros';
import { useNoteCardReads, withCardReads } from './services/use-note-card-reads';
import { InlineNoteLine } from './structured-note/inline-note-line';
import { AP_NARRATIVE_EDITORS } from './structured-note/note-phrases';

/** The line's label: the block's own words since Flutter (D-73: "Added notes" keeps its place). */
export const AP_NARRATIVE_LABEL = 'Added notes';
/** The DOM id of the field (focus target). */
export const AP_NARRATIVE_FIELD_ID = 'ap-narrative-line';
/** The pop-up's phrase editor id, so the same phrases are offered here. */

/** The action beside a narrative the line cannot edit losslessly (formatted): opens today's dialog. */
export const AP_NARRATIVE_EDIT_LABEL = 'Edit';

export interface ApInlineNarrativeProps {
  /** `notePlainText` (the page's `assessmentNote`). */
  note: string;
  /** `notes`, the stored Quill Delta (the page's `assessmentNoteDelta`). */
  delta?: string;
  /** The pop-up's save: `plain` → notePlainText, `delta` → notes. Absent → read-only. Kept while `readOnly`
   *  too, so a draft typed just before the note froze is handed over instead of dropped. */
  onChange?: (plain: string, delta: string) => void;
  /** Opens today's dialog, for a narrative the line cannot edit losslessly (a formatted one). */
  onOpenDialog?: () => void;
  readOnly?: boolean;
  /** The phrases, supplied (stories, tests). Absent → fetched for this visit, as the pop-up did. */
  macros?: readonly NoteMacro[];
  macroScope?: NoteMacroScope;
  chartReads?: ChartReads;
  /** PROD-2947 Part 2 (G8): the complaint's starter-map phrases to suggest first (never inserted). */
  suggested?: readonly string[];
}

export function ApInlineNarrative({ macros, macroScope, ...props }: ApInlineNarrativeProps) {
  const editable = !props.readOnly && !!props.onChange;
  // The phrase list and the lab card reads are fetched only while the line can be edited: a frozen or
  // read-only A&P costs no request (and needs no query client), as the closed pop-up did.
  // Integration (Codex P1): once the fetching path has mounted it stays the same component when the note freezes,
  // so the line is not unmounted by the freeze — an unmount hands its draft to the save, which would write into the
  // signed note. The frozen line holds its draft on screen instead (inline-note-line.tsx).
  const [fetching, setFetching] = useState(false);
  if (editable && !macros && !fetching) setFetching(true);
  if (!macros && (editable || fetching)) return <WithVisitPhrases {...props} macroScope={macroScope} />;
  return <NarrativeLine {...props} macros={macros} pieces={editable} />;
}

function WithVisitPhrases({ macroScope, ...props }: Omit<ApInlineNarrativeProps, 'macros'>) {
  const query = useNoteMacros(macroScope);
  // PROD-2947 Part 2: piece phrases are ON inside frame v2 (this line exists only there) — no extra switch. The pop-up
  // (switch OFF, and a formatted note's Edit) keeps reading `?ef=phrasePieces`.
  const pieces = true;
  const card = useNoteCardReads(pieces ? props.chartReads?.patientId || undefined : undefined);
  const chartReads = pieces && props.chartReads ? withCardReads(props.chartReads, card) : props.chartReads;
  return <NarrativeLine {...props} chartReads={chartReads} macros={query.data} pieces={pieces} />;
}

function NarrativeLine({
  note,
  delta,
  onChange,
  onOpenDialog,
  readOnly,
  macros,
  chartReads,
  pieces,
  suggested,
}: Omit<ApInlineNarrativeProps, 'macroScope'> & { pieces: boolean }) {
  const where = { patientId: chartReads?.patientId || undefined, encounterId: chartReads?.encounterId || undefined };
  const narrative = readInlineNarrative(delta, note, where);
  const canWrite = !readOnly && !!onChange;
  // Lossless only: a formatted narrative is never rewritten by the line; Edit opens the dialog instead.
  const editable = canWrite && narrative.editable;
  const dialogEdit = canWrite && !narrative.editable && !!onOpenDialog;
  return (
    // Design desk at 0bfe559 (L9, honest empty): empty, the line draws nothing, and this wrapper must not hold a slot
    // either: as an empty flex child it still took the canvas's 20px gap, a blank band under "Add diagnosis".
    // `empty:hidden` takes it out of the layout while it stays mounted, so the header's "Add note" still opens it.
    <div data-testid="ap-inline-narrative" data-inline-editable={editable ? 'true' : 'false'} className="empty:hidden">
      <InlineNoteLine
        id={AP_NARRATIVE_FIELD_ID}
        label={AP_NARRATIVE_LABEL}
        segs={narrative.segs}
        onCommit={(next) => {
          const wire = lineSegsToWire(next);
          onChange?.(wire.plain, wire.delta);
        }}
        readOnly={!editable}
        macros={macros}
        editors={AP_NARRATIVE_EDITORS}
        author="physician"
        pieces={pieces}
        chartReads={chartReads}
        owner={where}
        // L10: empty, it draws nothing; the block's header offers "Add note".
        start="block"
        suggested={suggested}
        footnote={
          dialogEdit ? (
            // In the line's value column, under the text it edits (the note's one label column, D-62).
            <NoteLine label="" align="center">
              <Button variant="tertiary" size="sm" onClick={onOpenDialog} aria-label={`${AP_NARRATIVE_EDIT_LABEL} ${AP_NARRATIVE_LABEL.toLowerCase()}`}>
                {AP_NARRATIVE_EDIT_LABEL}
              </Button>
            </NoteLine>
          ) : undefined
        }
      />
    </div>
  );
}
